import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, DatePicker, message, Space, Tag, Switch, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const Staff = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [commissionEnabled, setCommissionEnabled] = useState(false);

    // Helper function to format currency
    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    const handleEdit = (record) => {
        setEditingStaff(record);
        setCommissionEnabled(record.commission?.enabled || false);
        form.setFieldsValue({
            ...record,
            salaryAmount: record.salary?.amount || 0,
            currency: record.salary?.currency || 'USD',
            paymentFrequency: record.salary?.paymentFrequency,
            dateOfBirth: record.dateOfBirth ? dayjs(record.dateOfBirth) : null,
            joinDate: record.joinDate ? dayjs(record.joinDate) : null,
            commissionEnabled: record.commission?.enabled || false,
            commissionType: record.commission?.type || 'percentage',
            commissionPercentage: record.commission?.percentage || 0,
            commissionFixedAmount: record.commission?.fixedAmount || 0,
            commissionAppliesTo: record.commission?.appliesTo || 'course-income',
            specificCourses: record.commission?.specificCourses || [],
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('actionCannotBeUndone'),
            onOk: () => deleteMutation.mutate(id),
        });
    };
    // Filter states
    const [filterPosition, setFilterPosition] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: staffData, isLoading } = useQuery('staff', async () => {
        const response = await api.get('/staff');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: coursesData } = useQuery('courses-for-staff', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    });

    const createMutation = useMutation((data) => api.post('/staff', data), {
        onSuccess: () => {
            queryClient.invalidateQueries('staff');
            message.success(t('createdSuccessfully'));
            setIsModalVisible(false);
            form.resetFields();
        },
    });

    const updateMutation = useMutation(({ id, data }) => api.put(`/staff/${id}`, data), {
        onSuccess: () => {
            queryClient.invalidateQueries('staff');
            message.success(t('updatedSuccessfully'));
            setIsModalVisible(false);
            form.resetFields();
        },
    });

    const deleteMutation = useMutation((id) => api.delete(`/staff/${id}`), {
        onSuccess: () => {
            queryClient.invalidateQueries('staff');
            message.success(t('deletedSuccessfully'));
        },
    });

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            dateOfBirth: values.dateOfBirth?.toISOString(),
            joinDate: values.joinDate?.toISOString(),
            staffId: values.staffId || `STF${Math.floor(100000 + Math.random() * 900000)}`,
            salary: {
                amount: values.salaryAmount,
                currency: values.currency || 'USD',
                paymentFrequency: values.paymentFrequency || 'monthly',
            },
            commission: {
                enabled: values.commissionEnabled || false,
                type: values.commissionType || 'percentage',
                percentage: values.commissionPercentage || 0,
                fixedAmount: values.commissionFixedAmount || 0,
                appliesTo: values.commissionAppliesTo || 'course-income',
                specificCourses: values.specificCourses || [],
            },
        };
        delete formattedValues.salaryAmount;
        delete formattedValues.currency;
        delete formattedValues.paymentFrequency;
        delete formattedValues.commissionEnabled;
        delete formattedValues.commissionType;
        delete formattedValues.commissionPercentage;
        delete formattedValues.commissionFixedAmount;
        delete formattedValues.commissionAppliesTo;
        delete formattedValues.specificCourses;

        if (editingStaff) {
            updateMutation.mutate({ id: editingStaff._id, data: formattedValues });
        } else {
            createMutation.mutate(formattedValues);
        }
    };

    const columns = [
        {
            title: t('staffId'),
            dataIndex: 'staffId',
            key: 'staffId',
        },
        {
            title: t('name'),
            key: 'name',
            render: (_, record) => (
                <a
                    onClick={() => navigate(`/staff/${record._id}`)}
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                    {record.firstName} {record.lastName}
                </a>
            ),
        },
        {
            title: t('position'),
            dataIndex: 'position',
            key: 'position',
            filters: [
                { text: t('teacher'), value: 'teacher' },
                { text: t('admin'), value: 'admin' },
                { text: t('reception'), value: 'reception' },
                { text: t('other'), value: 'other' },
            ],
        },
        {
            title: t('salary'),
            key: 'salary',
            render: (_, r) => formatCurrency(r.salary?.amount, r.salary?.currency),
        },
        {
            title: t('commission'),
            key: 'commission',
            render: (_, record) => {
                if (!record.commission?.enabled) {
                    return <Tag color="default">{t('none')}</Tag>;
                }
                if (record.commission.type === 'percentage') {
                    return (
                        <Tag color="blue">
                            {record.commission.percentage}% {t('of')} {
                                record.commission.appliesTo === 'course-income' ? t('courseIncome') :
                                    record.commission.appliesTo === 'all-income' ? t('allIncome') :
                                        t('specificCourses')
                            }
                        </Tag>
                    );
                } else {
                    return (
                        <Tag color="green">
                            {formatCurrency(record.commission.fixedAmount, record.salary?.currency)} {t('fixed')}
                        </Tag>
                    );
                }
            },
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: t('active'), value: 'active' },
                { text: t('inactive'), value: 'inactive' },
            ],
            render: (status) => {
                const color = status === 'active' ? 'green' : 'red';
                return <Tag color={color}>{status?.toUpperCase()}</Tag>;
            },
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record._id)} />
                </Space>
            ),
        },
    ];

    // Filtering logic
    let filteredStaff = staffData || [];
    if (filterPosition) {
        filteredStaff = filteredStaff.filter(staff => staff.position === filterPosition);
    }
    if (filterStatus) {
        filteredStaff = filteredStaff.filter(staff => staff.status === filterStatus);
    }
    if (searchText) {
        filteredStaff = filteredStaff.filter(staff =>
        (`${staff.firstName} ${staff.lastName}`.toLowerCase().includes(searchText.toLowerCase()) ||
            staff.email?.toLowerCase().includes(searchText.toLowerCase()))
        );
    }

    return (
        <div>
            <div className="page-header">
                <h1>{t('staff')}</h1>
                <Space className="filter-section" wrap>
                    <Select
                        placeholder={t('filterByPosition')}
                        allowClear
                        style={{ width: 140 }}
                        value={filterPosition}
                        onChange={setFilterPosition}
                    >
                        <Select.Option value="teacher">{t('teacher')}</Select.Option>
                        <Select.Option value="admin">{t('admin')}</Select.Option>
                        <Select.Option value="reception">{t('reception')}</Select.Option>
                        <Select.Option value="other">{t('other')}</Select.Option>
                    </Select>
                    <Select
                        placeholder={t('filterByStatus')}
                        allowClear
                        style={{ width: 120 }}
                        value={filterStatus}
                        onChange={setFilterStatus}
                    >
                        <Select.Option value="active">{t('active')}</Select.Option>
                        <Select.Option value="inactive">{t('inactive')}</Select.Option>
                    </Select>
                    <Input.Search
                        placeholder={t('searchByNameOrEmail')}
                        allowClear
                        style={{ width: 200 }}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingStaff(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                    >
                        {t('addStaff')}
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredStaff}
                rowKey="_id"
                loading={isLoading}
            />

            <Modal
                title={editingStaff ? t('editStaff') : t('addStaff')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingStaff(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(600px, 95vw)"
                centered
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {/* <Form.Item
                        name="staffId"
                        label={t('staffId')}
                        tooltip={t('autoGeneratedIfLeftBlank')}
                    >
                        <Input placeholder="Auto-generated if left blank" />
                    </Form.Item> */}

                    <Form.Item
                        name="firstName"
                        label={t('firstName')}
                        rules={[{ required: true, message: t('pleaseEnterFirstName') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="lastName"
                        label={t('lastName')}
                        rules={[{ required: true, message: t('pleaseEnterLastName') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('email')}
                        rules={[
                            { type: 'email', message: t('pleaseEnterValidEmail') }
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={t('phone')}
                        rules={[{ required: true, message: t('pleaseEnterPhone') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="dateOfBirth"
                        label={t('dateOfBirth')}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="position"
                        label={t('position')}
                        rules={[{ required: true, message: 'Please select position' }]}
                    >
                        <Select>
                            <Select.Option value="teacher">{t('teacher')}</Select.Option>
                            <Select.Option value="admin">{t('admin')}</Select.Option>
                            <Select.Option value="reception">{t('reception')}</Select.Option>
                            <Select.Option value="other">{t('other')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="salaryAmount"
                        label={t('salary')}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={t('currency')}
                        rules={[{ required: true, message: 'Please select currency' }]}
                        initialValue="USD"
                    >
                        <Select>
                            <Select.Option value="USD">{t('USD')}</Select.Option>
                            <Select.Option value="IQD">{t('IQD')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="paymentFrequency"
                        label={t('paymentFrequency')}
                    >
                        <Select defaultValue="monthly">
                            <Select.Option value="monthly">{t('monthly')}</Select.Option>
                            <Select.Option value="weekly">{t('weekly')}</Select.Option>
                            <Select.Option value="daily">{t('daily')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Divider orientation="left">{t('commissionSettings')}</Divider>

                    <Form.Item
                        name="commissionEnabled"
                        label={t('enableCommission')}
                        valuePropName="checked"
                    >
                        <Switch onChange={(checked) => setCommissionEnabled(checked)} />
                    </Form.Item>

                    {commissionEnabled && (
                        <>
                            <Form.Item
                                name="commissionType"
                                label={t('commissionType')}
                                initialValue="percentage"
                            >
                                <Select>
                                    <Select.Option value="percentage">{t('percentage')}</Select.Option>
                                    <Select.Option value="fixed">{t('fixedAmount')}</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.commissionType !== currentValues.commissionType}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('commissionType') === 'percentage' ? (
                                        <Form.Item
                                            name="commissionPercentage"
                                            label={t('commissionPercentage')}
                                            rules={[{ required: commissionEnabled, message: 'Please enter percentage' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                style={{ width: '100%' }}
                                                addonAfter="%"
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            name="commissionFixedAmount"
                                            label={t('fixedCommissionAmount')}
                                            rules={[{ required: commissionEnabled, message: 'Please enter amount' }]}
                                        >
                                            <InputNumber min={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>

                            <Form.Item
                                name="commissionAppliesTo"
                                label={t('appliesTo')}
                                initialValue="course-income"
                            >
                                <Select>
                                    <Select.Option value="course-income">{t('courseIncome')}</Select.Option>
                                    <Select.Option value="all-income">{t('allIncome')}</Select.Option>
                                    <Select.Option value="specific-courses">{t('specificCourses')}</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.commissionAppliesTo !== currentValues.commissionAppliesTo}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('commissionAppliesTo') === 'specific-courses' && (
                                        <Form.Item
                                            name="specificCourses"
                                            label={t('selectCourses')}
                                            rules={[{ required: getFieldValue('commissionAppliesTo') === 'specific-courses', message: 'Please select courses' }]}
                                        >
                                            <Select mode="multiple" placeholder={t('selectCourses')}>
                                                {coursesData?.map(course => (
                                                    <Select.Option key={course._id} value={course._id}>
                                                        {course.name} ({course.code})
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>
                        </>
                    )}

                    <Divider />

                    <Form.Item
                        name="joinDate"
                        label={t('joinDate')}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label={t('status')}
                        initialValue="active"
                    >
                        <Select>
                            <Select.Option value="active">{t('active')}</Select.Option>
                            <Select.Option value="inactive">{t('inactive')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingStaff ? t('update') : t('create')}
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                setEditingStaff(null);
                                form.resetFields();
                            }}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default Staff;
